import { Button, LinearProgress, Typography } from '@mui/material';
import { useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { usePresignedUrl } from '../../services/usePreSignedUrl';
import { Videocam, AudioFile } from '@mui/icons-material';

interface FileUploadProps {
    source: string;
    type: string;
    typeId?: string;
    test_id?: string | number;
    multiple?: boolean;
    accept?: string;
    label?: string;
    meta?: any;
    required?: boolean;
    selectedUrl?: string;
    setSelectedUrl?: (url: string) => void;
    onChange?: (value: string | string[]) => void;
}

export const uploadFileToS3 = async (
    file: Blob,
    type: string,
    s3Url: string,
    onProgress: (percent: number) => void,
): Promise<any> => {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', s3Url, true);
        xhr.setRequestHeader('Content-Type', type);

        xhr.upload.onprogress = event => {
            if (event.lengthComputable) {
                const percent = Math.round((event.loaded * 100) / event.total);
                onProgress(percent);
            }
        };

        xhr.onload = () => {
            if (xhr.status === 200 || xhr.status === 201) resolve(xhr);
            else reject(new Error(`Upload failed: ${xhr.status}`));
        };

        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(file);
    });
};

const FileUpload: React.FC<FileUploadProps> = ({
    source,
    type,
    typeId,
    multiple = false,
    accept = 'image/*',
    label,
    meta,
    required = false,
    selectedUrl,
    setSelectedUrl,
    onChange,
}) => {
    const formContext = useFormContext();

    const i = useController({
        name: source,
        control: formContext?.control,
        rules: {
            validate: value => {
                if (
                    required &&
                    (!value || (multiple && Array.isArray(value) && value.length === 0))
                ) {
                    return `${label || 'File'} is required`;
                }
                return true;
            },
        },
    });

    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);

    const { getPresignedUrl } = usePresignedUrl();

    const showUpload = !loading && (!i.field.value || (multiple && i.field.value.length === 0));

    // ---------- PREVIEW UI ----------
    const renderPreview = () => {
        const url = selectedUrl || (multiple ? i.field.value?.[0] : i.field.value);
        if (!url) return null;

        const lower = url.toLowerCase();
        const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/.test(lower);
        const isPDF = lower.endsWith('.pdf');
        const isAudio = /\.(mp3|wav|ogg|m4a|flac|aac)$/.test(lower);

        return (
            <div className="flex flex-col items-start gap-2">
                {isImage && (
                    <img
                        src={url}
                        alt="preview"
                        style={{
                            width: '150px',
                            height: 'auto',
                            borderRadius: '8px',
                            border: '1px solid #ddd',
                        }}
                    />
                )}

                {isPDF && (
                    <div className="flex items-center gap-2">
                        <img
                            src="https://cdn-icons-png.flaticon.com/512/337/337946.png"
                            alt="pdf"
                            width={40}
                        />
                        <Typography variant="body2">{url.split('/').pop()}</Typography>
                    </div>
                )}

                {isAudio && (
                    <div className="flex items-center gap-2">
                        <AudioFile sx={{ fontSize: '80px' }} />
                        <Typography variant="body2">{url.split('/').pop()}</Typography>
                    </div>
                )}

                {!isImage && !isPDF && !isAudio && <Videocam sx={{ fontSize: '80px' }} />}

                <Button
                    color="error"
                    onClick={() => {
                        i.field.onChange(multiple ? [] : '');
                        setSelectedUrl?.('');
                    }}
                >
                    Remove
                </Button>
            </div>
        );
    };

    // ---------- UPLOAD HANDLER ----------
    const handleFileChange = async (file: File) => {
        try {
            setLoading(true);
            setProgress(0);

            const resp = await getPresignedUrl(type, file.name, typeId);

            if (!resp?.url || !resp.key || !resp.baseUrl)
                throw new Error('Invalid presigned URL response');

            const uploadUrl = resp.url;
            const publicUrl = `${resp.baseUrl}/${resp.key}`;

            await uploadFileToS3(file, file.type, uploadUrl, setProgress);

            if (meta && resp.metaUrl) {
                const jsonBlob = new Blob([JSON.stringify(meta, null, 2)], {
                    type: 'application/json',
                });
                await uploadFileToS3(jsonBlob, 'application/json', resp.metaUrl, () => {});
            }

            if (multiple) {
                const prev = i.field.value || [];
                const updated = [...prev, publicUrl];
                i.field.onChange(updated);
                setSelectedUrl?.(publicUrl);
                onChange?.(updated);
            } else {
                i.field.onChange(publicUrl);
                setSelectedUrl?.(publicUrl);
                onChange?.(publicUrl);
            }
        } catch (err) {
            console.error('Upload failed:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="my-2 w-full">
            {!showUpload && !loading && (
                <div className="p-2 rounded-md border w-full bg-gray-50">{renderPreview()}</div>
            )}

            {loading && (
                <div className="p-4 rounded-md border bg-gray-50 w-full">
                    <Typography>{progress}%</Typography>
                    <LinearProgress value={progress} variant="determinate" color="secondary" />
                </div>
            )}

            {showUpload && (
                <div className="p-1 rounded-md bg-gray-50 border w-full relative overflow-hidden">
                    <Button>{label || `Select ${source}`}</Button>
                    <input
                        type="file"
                        accept={accept}
                        multiple={multiple}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: 0,
                            cursor: 'pointer',
                        }}
                        onChange={async e => {
                            const files = e.target.files;
                            if (!files) return;
                            for (let i = 0; i < files.length; i++) {
                                await handleFileChange(files[i]);
                            }
                        }}
                    />
                </div>
            )}

            {i.fieldState.error && (
                <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                    {i.fieldState.error.message || 'File upload required'}
                </Typography>
            )}
        </div>
    );
};

export default FileUpload;
